import { Menu } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import { BackToTop } from '../../ui/BackToTop';
import { TooltipProvider } from '../../ui/Tooltip';
import { SidebarContent } from './SidebarContent';

// Per-item glyph tint (component/sidebar/icon/color/*); selected rows override
// it with the dark on-accent color.
export type SidebarAccent = 'sky' | 'indigo' | 'apricot' | 'fuchsia';
// Trailing status pill (New!/Beta/Soon), mapped to the Badge variants of the
// same name.
export type SidebarBadge = 'new' | 'beta' | 'soon';

// A top-level entry. Usually navigable (has `path`); `disabled` (or a missing
// `path`) renders it as greyed, non-interactive text — a page not available yet.
export type SidebarLeafItem = {
  label: string;
  path?: string;
  icon?: ReactNode;
  end?: boolean;
  accent?: SidebarAccent;
  badge?: SidebarBadge;
  disabled?: boolean;
};

// A page nested under a group — no icon, rendered in the submenu. `disabled`
// (or a missing `path`) renders it as non-interactive text: a page in the design
// that isn't available yet.
export type SidebarChildItem = {
  label: string;
  path?: string;
  end?: boolean;
  badge?: SidebarBadge;
  disabled?: boolean;
};

// An expandable group header with a submenu; not navigable itself.
export type SidebarGroupItem = {
  label: string;
  icon?: ReactNode;
  accent?: SidebarAccent;
  badge?: SidebarBadge;
  children: SidebarChildItem[];
};

// Discriminated by `children` (group) vs `path` (leaf).
export type SidebarNavItem = SidebarLeafItem | SidebarGroupItem;

export type SidebarNavigationType = Array<{ title: string; items: SidebarNavItem[] }>;

// `pathname` lets the host vary the item set by section (e.g. admin reuses this
// sidebar with admin entries). `state` is `unknown` so libs/shared takes no
// dependency on the host store shape.
export type SidebarNavigationFactory = (state: unknown, pathname: string) => SidebarNavigationType;

// Host-provided so the footer can wire host-specific Notifications/Profile;
// `collapsed` lets the host render icon-only rows on the collapsed rail, and
// `drawer` lets it render a purpose-built mobile layout in the off-canvas menu.
export type SidebarSlot = (ctx: { collapsed: boolean; drawer: boolean }) => ReactNode;

// Everything about a nav that changes what is rendered, minus the icons.
function navSignature(nav: SidebarNavigationType): string {
  return JSON.stringify(
    nav.map((group) => ({
      t: group.title,
      i: group.items.map((item) =>
        'children' in item
          ? {
              l: item.label,
              a: item.accent,
              b: item.badge,
              c: item.children.map((child) => ({
                l: child.label,
                p: child.path,
                e: child.end,
                b: child.badge,
                d: child.disabled,
              })),
            }
          : {
              l: item.label,
              p: item.path,
              e: item.end,
              a: item.accent,
              b: item.badge,
              d: item.disabled,
            },
      ),
    })),
  );
}

const COLLAPSE_STORAGE_KEY = 'eo:sidebar-collapsed';
// Below this the rail is collapsed and expanding overlays the content; at/above it
// the expanded rail is in-flow and pushes the content. Matches `xl`, where the rail
// first appears at all — above the DS's 90rem, the rail existed but could never be
// expanded in place, so the stored "expanded" preference was silently overridden.
const PUSH_QUERY = '(min-width: 80rem)';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

type Props = {
  appTitle: string;
  sidebarNavigation: SidebarNavigationType | SidebarNavigationFactory;
  appLogo?: ReactNode;
  // Compact brand mark shown as the expand trigger on the collapsed rail.
  appLogoCollapsed?: ReactNode;
  sidebarFooter?: SidebarSlot;
  sidebarBottomBar?: SidebarSlot;
  headerContent?: ReactNode;
  headerUtilities?: ReactNode;
  // Predicate controlling whether sidebar + header chrome render for the
  // current route. Default: chrome on. Hosts pass a predicate to suppress
  // chrome on auth callbacks, embed iframes, public invitation pages, etc.
  shouldShowSidebar?: (pathname: string) => boolean;
  // Optional in-flow right column (e.g. an assistant side panel). A flex
  // sibling of the main column, so it pushes content instead of overlaying it.
  rightPanel?: ReactNode;
  children: ReactNode;
};

export function Layout({
  appTitle,
  sidebarNavigation,
  appLogo,
  appLogoCollapsed,
  sidebarFooter,
  sidebarBottomBar,
  headerContent,
  headerUtilities,
  shouldShowSidebar,
  rightPanel,
  children,
}: Props) {
  const { pathname } = useLocation();
  // A nav factory builds a fresh array per call, so an unmemoized selector hands
  // back a new reference on every dispatch anywhere in the store — React-Redux
  // flags it, and the whole shell re-renders on every API call. The signature
  // comparison keeps the previous reference whenever the rebuilt nav is
  // equivalent, so only a real change (an item appearing, a badge flipping)
  // re-renders. Icons are excluded deliberately: they are React elements, so a
  // factory mints new ones per call and they would never compare equal.
  const navCache = useRef<{
    state: unknown;
    pathname: string;
    signature: string;
    nav: SidebarNavigationType;
  } | null>(null);

  const computeNav = useCallback(
    (state: unknown): SidebarNavigationType => {
      if (typeof sidebarNavigation !== 'function') return sidebarNavigation;

      const cached = navCache.current;
      if (cached && cached.state === state && cached.pathname === pathname) return cached.nav;

      const next = sidebarNavigation(state, pathname);
      const signature = navSignature(next);

      if (cached && cached.pathname === pathname && cached.signature === signature) {
        navCache.current = { state, pathname, signature, nav: cached.nav };
        return cached.nav;
      }

      navCache.current = { state, pathname, signature, nav: next };
      return next;
    },
    [sidebarNavigation, pathname],
  );

  const computedNav = useSelector(computeNav);
  const showChrome = shouldShowSidebar ? shouldShowSidebar(pathname) : true;

  // Push mode: the rail is in-flow and the persisted toggle expands it in place.
  // Below it the rail is always collapsed and expanding is an overlay.
  const isPush = useMediaQuery(PUSH_QUERY);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === 'true';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  // The expanded sidebar as an overlay: the <xl hamburger and the 1280–1439 rail
  // expand affordance both open it; it never opens in push mode.
  const [overlayOpen, setOverlayOpen] = useState(false);
  useEffect(() => setOverlayOpen(false), [pathname]);
  useEffect(() => {
    if (isPush) setOverlayOpen(false);
  }, [isPush]);
  useEffect(() => {
    if (!overlayOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOverlayOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlayOpen]);

  if (!showChrome) {
    return (
      <main data-testid="app-main" className="min-h-screen bg-background text-foreground">
        {children}
      </main>
    );
  }

  // Section titles are intentionally dropped — the DS nav is a single flat list.
  const navItems = computedNav.flatMap((section) => section.items);

  // In-flow rail is collapsed unless we're in push mode AND the user expanded it.
  const railCollapsed = isPush ? collapsed : true;
  // The rail's expand affordance toggles in place when pushing, overlays otherwise.
  const onRailExpand = () => (isPush ? setCollapsed((c) => !c) : setOverlayOpen(true));

  const sidebarShell =
    'flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground';

  return (
    <div
      data-testid="app-layout"
      className={cn(
        // Live sidebar width, for overlays that must cover only the content
        // region (e.g. the chatbot's expanded view). 0 below xl (hamburger).
        'flex min-h-screen bg-background text-foreground [--app-sidebar-width:0px]',
        railCollapsed ? 'xl:[--app-sidebar-width:4rem]' : 'xl:[--app-sidebar-width:16rem]',
      )}
    >
      <TooltipProvider delayDuration={0}>
        {/* Persistent rail — shown from xl up; below xl it's the hamburger drawer.
         * w-16 except when expanded in push mode, where it widens to push. */}
        <aside
          aria-label="Primary navigation"
          data-testid="app-sidebar"
          data-collapsed={railCollapsed}
          className={cn(
            'sticky top-0 hidden h-screen shrink-0 xl:flex',
            sidebarShell,
            railCollapsed ? 'w-16' : 'w-64',
          )}
        >
          <SidebarContent
            variant="fixed"
            collapsed={railCollapsed}
            onToggleCollapse={onRailExpand}
            onClose={() => setOverlayOpen(false)}
            appTitle={appTitle}
            appLogo={appLogo}
            appLogoCollapsed={appLogoCollapsed}
            navItems={navItems}
            sidebarFooter={sidebarFooter}
            sidebarBottomBar={sidebarBottomBar}
          />
        </aside>

        {/* Expanded overlay — the <xl drawer and the 1280–1439 expand both use it.
         * Rendered only while open so the nav/footer slots aren't duplicated. */}
        {overlayOpen && (
          <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
            <div
              data-testid="sidebar-drawer-overlay"
              className="absolute inset-0 bg-black/50"
              onClick={() => setOverlayOpen(false)}
            />
            <aside
              aria-label="Primary navigation"
              data-testid="app-sidebar-drawer"
              className={cn(
                // Phones get a full-screen menu (purpose-built mobile, not the
                // desktop rail shrunk — matches legacy's full-screen Sheet);
                // tablets/small laptops get a 288px panel overlay.
                'absolute left-0 top-0 flex h-full w-full sm:w-72',
                sidebarShell,
              )}
            >
              <SidebarContent
                variant="drawer"
                collapsed={false}
                onToggleCollapse={onRailExpand}
                onClose={() => setOverlayOpen(false)}
                appTitle={appTitle}
                appLogo={appLogo}
                appLogoCollapsed={appLogoCollapsed}
                navItems={navItems}
                sidebarFooter={sidebarFooter}
                sidebarBottomBar={sidebarBottomBar}
              />
            </aside>
          </div>
        )}
      </TooltipProvider>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          data-testid="app-header"
          className={cn(
            'sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-sidebar-border bg-background px-4 sm:px-6',
            'backdrop-blur supports-[backdrop-filter]:bg-background/80',
          )}
        >
          <button
            type="button"
            data-testid="sidebar-drawer-trigger"
            aria-label="Open navigation"
            onClick={() => setOverlayOpen(true)}
            className={cn(
              'flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg xl:hidden',
              'text-foreground transition-colors hover:bg-accent',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">{headerContent}</div>
          {headerUtilities && (
            <div className="flex shrink-0 items-center gap-2">{headerUtilities}</div>
          )}
        </header>

        <main data-testid="app-main" className="flex-1 bg-background">
          {children}
        </main>
        <BackToTop />
      </div>

      {rightPanel}
    </div>
  );
}
